/**
 * Linear Foot Selector
 * Provides slider and input controls for selecting linear footage of products
 * Integrates with Shopify cart system
 */

class LinearFootSelector {
  constructor(container) {
    this.container = container;
    this.slider = container.querySelector('[data-linear-foot-slider]');
    this.input = container.querySelector('[data-linear-foot-input]');
    this.pricePerFootEl = container.querySelector('[data-price-per-foot]');
    this.totalPriceEl = container.querySelector('[data-total-price]');
    this.quantityInput = container.querySelector('input[name="quantity"][data-quantity-input]');
    
    // Configuration
    this.min = parseInt(this.slider.min) || 5;
    this.max = parseInt(this.slider.max) || 20;
    this.default = parseInt(this.slider.dataset.default) || 10;
    this.pricePerFoot = parseFloat(this.container.dataset.pricePerFoot) || 0;
    
    this.init();
  }

  init() {
    // Set initial values
    this.setValue(this.default);
    this.updatePriceDisplay();
    
    // Bind events
    this.slider.addEventListener('input', () => this.handleSliderChange());
    this.input.addEventListener('input', () => this.handleInputChange());
    this.input.addEventListener('blur', () => this.validateInput());
    
    // Handle keyboard accessibility for slider
    this.slider.addEventListener('keydown', (e) => this.handleKeyboard(e));
  }

  handleSliderChange() {
    const value = parseInt(this.slider.value);
    this.input.value = value;
    if (this.quantityInput) {
      this.quantityInput.value = value;
    }
    this.updatePriceDisplay();
  }

  handleInputChange() {
    let value = parseInt(this.input.value);
    
    // Allow user to type, but constrain to range
    if (!isNaN(value)) {
      value = Math.max(this.min, Math.min(this.max, value));
      this.slider.value = value;
      if (this.quantityInput) {
        this.quantityInput.value = value;
      }
      this.updatePriceDisplay();
    }
  }

  validateInput() {
    let value = parseInt(this.input.value);
    
    // If invalid input, reset to default
    if (isNaN(value) || value < this.min || value > this.max) {
      value = this.default;
    }
    
    this.setValue(value);
    this.updatePriceDisplay();
  }

  handleKeyboard(e) {
    // Allow arrow keys to increment/decrement
    const currentValue = parseInt(this.slider.value);
    
    if (e.key === 'ArrowUp' || e.key === 'ArrowRight') {
      e.preventDefault();
      this.setValue(Math.min(this.max, currentValue + 1));
    } else if (e.key === 'ArrowDown' || e.key === 'ArrowLeft') {
      e.preventDefault();
      this.setValue(Math.max(this.min, currentValue - 1));
    }
  }

  setValue(value) {
    this.slider.value = value;
    this.input.value = value;
    if (this.quantityInput) {
      this.quantityInput.value = value;
    }
  }

  updatePriceDisplay() {
    const linearFeet = parseInt(this.slider.value);
    const totalPrice = this.pricePerFoot * linearFeet;
    
    // Format prices
    const formattedPricePerFoot = this.formatMoney(this.pricePerFoot * 100);
    const formattedTotalPrice = this.formatMoney(totalPrice * 100);
    
    // Update display
    if (this.pricePerFootEl) {
      this.pricePerFootEl.textContent = formattedPricePerFoot;
    }
    
    if (this.totalPriceEl) {
      this.totalPriceEl.textContent = formattedTotalPrice;
    }
    
    // Dispatch custom event for other components to listen
    this.container.dispatchEvent(new CustomEvent('linearFootChange', {
      detail: {
        linearFeet: linearFeet,
        pricePerFoot: this.pricePerFoot,
        totalPrice: totalPrice
      },
      bubbles: true
    }));
  }

  formatMoney(cents) {
    // Use Shopify's money format if available
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(cents, theme.moneyFormat || '${{amount}}');
    }
    
    // Fallback formatting
    const dollars = cents / 100;
    return '$' + dollars.toFixed(2);
  }

  getValue() {
    return parseInt(this.slider.value);
  }
}

// Initialize all linear foot selectors on the page
function initLinearFootSelectors() {
  const selectors = document.querySelectorAll('[data-linear-foot-selector]');
  
  selectors.forEach(container => {
    if (!container.linearFootSelector) {
      container.linearFootSelector = new LinearFootSelector(container);
    }
  });
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initLinearFootSelectors);
} else {
  initLinearFootSelectors();
}

// Re-initialize when dynamic content is loaded (for AJAX)
document.addEventListener('shopify:section:load', initLinearFootSelectors);

// Export for use in other scripts
window.LinearFootSelector = LinearFootSelector;
window.initLinearFootSelectors = initLinearFootSelectors;

